import { defineStore } from 'pinia';
import ViajesGrupoApiService from '@/services/api/ViajesGrupoApiService';
import { getPaqueteBasicoForServicio, getServicioFromAgrupaciones } from '@/helpers/serviciosUtils';
import {
  calculaDescuento,
  calculaDuracionViaje,
  calculaPrecioBase,
  calculaPrecioComplementos,
  calculaPrecioServicios,
  calculaPrecioGastos,
  calculaPrecioDesdeForEdad,
  calculaPrecioPlazasLibresPorOcupante,
  groupAgrupacionesServiciosByTarifa,
  getAgrupacionServiciosGroupedByTarifaByEdad,
  groupServiciosByCupoMetaServicio,
} from '@/helpers/viajesGrupos';
import { max, min } from '@popperjs/core/lib/utils/math';
import { ESTADOS_RESERVA, TIPO_COMPLEMENTO, TIPO_TASA } from '@/constants/reservaConstants';
import currency from 'currency.js';
import { readonly } from 'vue';
import { viajesGrupos } from '@/store_pinia/viajesGrupo';
import { useLoadingStore } from '@/store_pinia/loadingStore';
import { calcularEdad } from '@/helpers/dateUtiles';

export const informacionHabitacion = defineStore('informacionHabitacion', {
  state: () => ({
    reservas: [],
    //@deprecated
    descuento: 0,
    codigoDescuento: null,
    agrupacionesServicios: [], //VGAgrupacionServiciosDTO
    agrupacionesServiciosGroupedByTarifa: {},
    _cuposMetaServicios: {},
    informacionGrupo: {},
    informacionAlojamiento: {
      nombre: '',
      estrellas: 0,
      poblacion: '',
      cupos: [
        {
          tipo: {
            nombre: '',
          },
          plazas: 0,
          tipoRegimen: {
            id: 0,
            nombre: '',
          },
        },
      ],
      habitacion: [
        {
          codigo: '',
          id: 0,
          numPlazas: 0,
          numPlazasOcupadas: 0,
          plazas: 0,
          tipo: { id: 0, nombre: '' },
        },
      ],
      restriccionesEdad: [],
    },
    reservaRecuperada: null,
    informacionViaje: {},
  }),
  getters: {
    getCodigoDescuento(state) {
      return state.codigoDescuento;
    },
    getViaje(state) {
      return state.informacionViaje;
    },
    getAlojamiento(state) {
      return state.informacionAlojamiento;
    },
    getGrupo(state) {
      return state.informacionGrupo;
    },
    getAgrupacionesServicios(state) {
      return readonly(state.agrupacionesServicios);
    },
    getAgrupacionesServiciosGroupedByTarifa(state) {
      return state.agrupacionesServiciosGroupedByTarifa;
    },
    getAgrupacionesServiciosByReserva: state => {
      return reserva => {
        let edad = state.informacionViaje.defaultIntervalo.minEdad;
        if (reserva.usuario.fechaNacimiento) {
          edad = calcularEdad(reserva.usuario.fechaNacimiento);
        }
        return (
          getAgrupacionServiciosGroupedByTarifaByEdad(state.agrupacionesServiciosGroupedByTarifa, edad)
            ?.agrupacionesServicios ?? []
        );
      };
    },
    hasTarifaEdad(state) {
      // todo: seguramente en un futuro habra que tener en cuenta si hay tasa turistica
      return (
        state.agrupacionesServicios.some(agrupacion =>
          agrupacion.servicios?.some(servicio => servicio.activo && servicio.tarifaEdad)
        ) || state.informacionAlojamiento?.habitacion.sinLimitacionEdad === false
      );
    },
    getReservaById: state => {
      return reservaId => state.reservas.find(({ id }) => id === reservaId);
    },
    getReservaByIdx: state => {
      return reservaIdx => state.reservas[reservaIdx];
    },
    getReservaIdxById: state => {
      return reservaId => state.reservas.findIndex(({ id }) => id === reservaId);
    },
    isHabitacionVirtual(state) {
      return state.informacionAlojamiento?.habitacion.plazasReales > state.informacionAlojamiento?.habitacion.plazas;
    },
    getFeeTotalPlataforma(state) {
      return state.informacionViaje.repercutirFee ? state.informacionViaje.fee : 0;
    },
    getReservas(state) {
      return state.reservas;
    },
    getResumenReservas(state) {
      if (state.reservas.length === 0) return [];
      return state.reservas.map(reserva => {
        let reservaAux = {
          reservaPropia: false,
          tasasExternasCalculadas: [],
          tasasInternasCalculadas: [],
          usuario: null,
          nombreUsuario: '',
          ...reserva,
        };
        if (state.informacionAlojamiento) {
          if (state.informacionAlojamiento.habitacion.precios?.length > 1 && reservaAux.usuario.fechaNacimiento) {
            reservaAux.precioDesdeViaje = calculaPrecioDesdeForEdad(
              state.informacionAlojamiento.habitacion.precios,
              reservaAux
            );
          } else {
            reservaAux.precioDesdeViaje = state.informacionAlojamiento.precioDesde;
          }
        } else if (state.informacionViaje.preciosDesde?.length > 1 && reservaAux.usuario.fechaNacimiento) {
          reservaAux.precioDesdeViaje = calculaPrecioDesdeForEdad(state.informacionViaje.preciosDesde, reservaAux);
        } else {
          reservaAux.precioDesdeViaje = state.informacionViaje.precioDesde;
        }

        if (reservaAux.precioIncremento != null) {
          reservaAux.precioDesdeViaje =
            reservaAux.precioDesdeViaje - state.informacionViaje.precioIncremento + reservaAux.precioIncremento;
        }

        if (this.isHabitacionVirtual) {
          reservaAux.complementos = [
            {
              codigo: TIPO_COMPLEMENTO.PLAZAS_LIBRES,
              importe: calculaPrecioPlazasLibresPorOcupante(
                state.informacionAlojamiento.precioCamaVacia,
                state.informacionAlojamiento.habitacion.plazasReales - state.informacionAlojamiento.habitacion.plazas,
                state.informacionAlojamiento.habitacion.plazas
              ),
            },
          ];
        }

        reservaAux.precioServicios = calculaPrecioServicios(reservaAux);
        reservaAux.precioComplementos = calculaPrecioComplementos(reservaAux);
        reservaAux.precioGastos = calculaPrecioGastos(reservaAux);
        reservaAux.precioBase = calculaPrecioBase(reservaAux);
        reservaAux.precioTotal = reservaAux.precioBase.add(reservaAux.precioGastos);
        reservaAux.precioTotalSinTasasInternas = reservaAux.precioTotal;

        if (state.codigoDescuento !== null) {
          const totalPendiente = max(0, reservaAux.precioBase.subtract(reservaAux.totalPagado).value);
          reservaAux.descuento = currency(
            min(totalPendiente, calculaDescuento(state.codigoDescuento, reservaAux.precioBase).value)
          );
        } else {
          reservaAux.descuento = currency(0);
        }

        if (state.informacionAlojamiento?.tasaTuristica && state.informacionAlojamiento?.tasaTuristica !== 0) {
          let numDays = calculaDuracionViaje(state.informacionViaje);
          let precioTasaTuristica = currency(state.informacionAlojamiento.tasaTuristica).multiply(numDays);
          reservaAux.precioTotalSinTasasInternas = reservaAux.precioTotalSinTasasInternas.add(precioTasaTuristica);
          reservaAux.precioTotal = reservaAux.precioTotal.add(precioTasaTuristica);
          reservaAux.tasasExternasCalculadas.push({
            importe: precioTasaTuristica,
            codigo: TIPO_TASA.TASA_TURISTICA,
          });
        }

        const tasaTotal = this.getFeeTotalPlataforma;

        if (tasaTotal !== 0) {
          //Tasa gestion plataforma
          let precioTasaPlataforma = 0;
          //SI es de NUEVA CREACION o BLOQUEADA, la tasa se autocalcula
          //SI es MODIFICACION DE RESERVA, se mantiene
          if (!reservaAux.estado || reservaAux.estado === ESTADOS_RESERVA.BLOQUEADA) {
            precioTasaPlataforma = reservaAux.precioBase.multiply(tasaTotal);
          } else {
            const tasaPlataforma = reservaAux.tasasInternas.find(
              tasa => tasa.codigo === TIPO_TASA.TASA_GESTION_PLATAFORMA
            );
            precioTasaPlataforma = currency(tasaPlataforma ? tasaPlataforma.importe : 0);
          }
          reservaAux.precioTotal = reservaAux.precioTotal.add(precioTasaPlataforma);
          reservaAux.tasasInternasCalculadas.push({
            importe: precioTasaPlataforma,
            codigo: TIPO_TASA.TASA_GESTION_PLATAFORMA,
          });

          //Tasa gestion cambio
          if (reservaAux.tasasInternas) {
            let sumaTasasCambioBase = reservaAux.tasasInternas
              .filter(tasa => tasa.codigo === TIPO_TASA.TASA_GESTION_CAMBIO)
              .reduce((sum, tasa) => sum.add(tasa.importe), currency(0));
            reservaAux.precioTotal = reservaAux.precioTotal.add(sumaTasasCambioBase);
            if (sumaTasasCambioBase.value > 0) {
              reservaAux.tasasInternasCalculadas.push({
                importe: sumaTasasCambioBase,
                codigo: TIPO_TASA.TASA_GESTION_CAMBIO,
              });
            }
          }
        }

        reservaAux.serviciosWithCantidad = Object.keys(reserva.serviciosWithCantidad).map(key => {
          return reserva.serviciosWithCantidad[key];
        });
        return reservaAux;
      });
    },
    getUsuariosReserva(state) {
      return state.reservas.map(reserva => {
        return {
          nombre: reserva.usuario?.nombre || '',
          apellido: reserva.usuario?.apellido || '',
          done: false,
        };
      });
    },
    getNombresReserva(state) {
      return state.reservas.map((res, index) => {
        if (res['usuario'] === undefined) {
          return {
            id: res.id,
            nombre: res.usuarioNombre,
            index: index,
          };
        }
        return { id: res.id, nombre: res.usuario.nombre, index: index };
      });
    },
  },
  actions: {
    setCodigoDescuento(codigoDescuentoData) {
      this.codigoDescuento = codigoDescuentoData;
    },
    addNotPersistedReserva(reservaData) {
      let data = {
        persisted: false,
        hasConflictoEdad: false,
        serviciosWithCantidad: {},
        gastos: [],
        complementos: [],
        ...reservaData,
      };
      this.reservas.push(data);
    },
    deleteReserva(id) {
      this.reservas.splice(
        this.reservas.findIndex(data => data.id === id),
        1
      );
    },
    async loadInformacionViaje(
      viajeId,
      codigoViaje = null,
      forceReloadServicio = false,
      codigoPrevisualizacion = null
    ) {
      const loadingStore = useLoadingStore();
      if (
        this.informacionViaje === undefined ||
        Object.keys(this.informacionViaje).length === 0 ||
        this.informacionViaje.id !== viajeId
      ) {
        try {
          loadingStore.show();
          await Promise.all([
            viajesGrupos()
              .fetchViajeSeleccionado(viajeId, codigoViaje, codigoPrevisualizacion)
              .then(res => {
                this.informacionViaje = res;
              }),
            ViajesGrupoApiService.getServicios(viajeId, codigoViaje, codigoPrevisualizacion).then(res => {
              this.agrupacionesServicios = res.agrupacionesServicios;
              this.agrupacionesServiciosGroupedByTarifa = groupAgrupacionesServiciosByTarifa(
                this.agrupacionesServicios
              );
              this._cuposMetaServicios = groupServiciosByCupoMetaServicio(this.agrupacionesServicios);
            }),
          ]);
        } finally {
          loadingStore.hide();
        }
      } else if (forceReloadServicio) {
        try {
          loadingStore.show();
          await ViajesGrupoApiService.getServicios(viajeId, codigoViaje, codigoPrevisualizacion).then(res => {
            this.agrupacionesServicios = res.agrupacionesServicios;
            this.agrupacionesServiciosGroupedByTarifa = groupAgrupacionesServiciosByTarifa(res.agrupacionesServicios);
            this._cuposMetaServicios = groupServiciosByCupoMetaServicio(this.agrupacionesServicios);
          });
        } finally {
          loadingStore.hide();
        }
      }
    },
    setInformacionAlojamientoFromVGAlojamientoReservaDTO(vgAlojamientoReservaDTO) {
      const { numPlazas, plazasVirtuales } = vgAlojamientoReservaDTO.habitacion;
      const isCreadorHabitacionVirtual = plazasVirtuales != null && this.informacionGrupo.ocupantesPrevios.length === 0;
      this.informacionAlojamiento = {};
      this.informacionAlojamiento.id = vgAlojamientoReservaDTO.id;
      this.informacionAlojamiento.estrellas = vgAlojamientoReservaDTO.estrellas;
      this.informacionAlojamiento.incremento = vgAlojamientoReservaDTO.incremento || 0;
      this.informacionAlojamiento.nombre = vgAlojamientoReservaDTO.nombre;
      this.informacionAlojamiento.poblacion = vgAlojamientoReservaDTO.poblacion;
      this.informacionAlojamiento.precioCamaVacia = vgAlojamientoReservaDTO.precioCamaVacia;
      this.informacionAlojamiento.tasaTuristica = vgAlojamientoReservaDTO.tasaTuristica;
      this.informacionAlojamiento.hasHabitaciones = vgAlojamientoReservaDTO.hasHabitaciones;
      this.informacionAlojamiento.precioDesde = vgAlojamientoReservaDTO.precioDesde;
      this.informacionAlojamiento.fechaInicio = vgAlojamientoReservaDTO.fechaInicio;
      this.informacionAlojamiento.fechaFin = vgAlojamientoReservaDTO.fechaFin;
      this.informacionAlojamiento.habitacion = {
        disponibles: vgAlojamientoReservaDTO.habitacion.plazasDisponibles,
        precios: vgAlojamientoReservaDTO.precios,
        sinLimitacionEdad: vgAlojamientoReservaDTO.sinLimitacionEdad,
        ...vgAlojamientoReservaDTO.habitacion,
        ...(isCreadorHabitacionVirtual && {
          numPlazas: plazasVirtuales,
          plazas: plazasVirtuales,
          plazasReales: numPlazas,
        }),
      };
    },
    setInformacionAlojamientoFromRGrupoDTO(rGrupoDTO) {
      if (rGrupoDTO.habitacion) {
        this.informacionAlojamiento = {};
        this.informacionAlojamiento.id = rGrupoDTO.habitacion.alojamiento.id;
        this.informacionAlojamiento.estrellas = rGrupoDTO.habitacion.alojamiento.estrellas;
        this.informacionAlojamiento.incremento = rGrupoDTO.habitacion.alojamiento.incremento || 0;
        this.informacionAlojamiento.nombre = rGrupoDTO.habitacion.alojamiento.nombre;
        this.informacionAlojamiento.poblacion = rGrupoDTO.habitacion.alojamiento.poblacion;
        this.informacionAlojamiento.precioCamaVacia = rGrupoDTO.habitacion.alojamiento.precioCamaVacia;
        this.informacionAlojamiento.tasaTuristica = rGrupoDTO.habitacion.alojamiento.tasaTuristica;
        this.informacionAlojamiento.hasHabitaciones = !rGrupoDTO.habitacion.isUnica;
        this.informacionAlojamiento.precioDesde = rGrupoDTO.habitacion.alojamiento.precioDesde;
        this.informacionAlojamiento.fechaInicio = rGrupoDTO.habitacion.alojamiento.fechaInicio;
        this.informacionAlojamiento.fechaFin = rGrupoDTO.habitacion.alojamiento.fechaFin;
        this.informacionAlojamiento.habitacion = {
          tipo: {
            nombre: rGrupoDTO.habitacion.alojamiento.tipo,
          },
          ocupantes: rGrupoDTO.ocupantesNoGestionados,
          sinLimitacionEdad: rGrupoDTO.habitacion.alojamiento.sinLimitacionEdad,
          precios: rGrupoDTO.habitacion.alojamiento.precios,
          ...rGrupoDTO.habitacion,
        };
      } else {
        this.clearInformacionAlojamiento();
      }
    },
    // todo@jorge: evaluar si tiene sentido este metodo o se deberia utilizar otro a pesar de necesitar una llamada extra a la API
    setInformacionAlojamientoFromVGAlojamientoViajeDTO(vgAlojamientoViajeDTO) {
      const cupo = vgAlojamientoViajeDTO.cupos[0];
      this.informacionAlojamiento = vgAlojamientoViajeDTO;
      this.informacionAlojamiento.incremento = vgAlojamientoViajeDTO.incremento || 0;
      this.informacionAlojamiento.hasHabitaciones = cupo.hasHabitaciones;
      this.informacionAlojamiento.restriccionesEdad = cupo.restriccionesEdad || [];
      this.informacionAlojamiento.habitacion = {
        numPlazas: cupo.plazas,
        numPlazasOcupadas: 0,
        plazasLibresRepercutidas: false,
        plazasLibresEnabled: true,
        ocupantes: [],
        ...cupo,
      };
    },
    clearInformacionAlojamiento() {
      this.informacionAlojamiento = null;
    },
    setInformacionGrupoFromVGGrupoDTO(vgGrupoDTO) {
      this.informacionGrupo = vgGrupoDTO;
    },
    clearReservas() {
      this.reservas = [];
    },
    addReserva(reservaDto) {
      let reserva = {
        persisted: true,
        hasConflictoEdad: false,
        ...reservaDto,
      };
      let serviciosByAgrupacion = {};
      reservaDto.serviciosWithCantidad.forEach(swc => {
        const agrupacionesServicios = this.getAgrupacionesServiciosByReserva(reserva);
        const servicioPB = getPaqueteBasicoForServicio(agrupacionesServicios, swc.servicio);
        serviciosByAgrupacion[swc.servicio.agrupacion.id] = {
          ...swc,
          categoriaPaqueteBasico: servicioPB !== null,
          servicioPB: servicioPB,
          // The service needs to be override with the reactive one
          servicio: getServicioFromAgrupaciones(agrupacionesServicios, swc.servicio),
        };
      });
      reserva.usuario = {
        ...reservaDto.usuario,
        // todo-> Use apellidos instead of apellido
        apellido: reservaDto.usuario.apellidos,
      };
      // The servicios needs to be index to avoid duplicates
      reserva.serviciosWithCantidad = serviciosByAgrupacion;
      this.reservas.push(reserva);
    },
    addServicioToReserva(idxReserva, servicio, cantidad = 1) {
      const reserva = this.reservas[idxReserva];
      const servicioPB = getPaqueteBasicoForServicio(this.getAgrupacionesServiciosByReserva(reserva), servicio);
      reserva.serviciosWithCantidad[servicio.agrupacion.id] = {
        servicio: servicio,
        cantidad: cantidad,
        categoriaPaqueteBasico: servicioPB !== null,
        servicioPB: servicioPB,
      };
    },
    incrementCupoServicios(servicio, incremento = 1) {
      if (servicio) {
        servicio.cuposMetasId.forEach(cupoId => {
          this._cuposMetaServicios[cupoId].forEach(s => (s.numCupos += incremento));
        });
      }
    },
    decrementCupoServicios(servicio, decremento = 1) {
      if (servicio) {
        servicio.cuposMetasId.forEach(cupoId => {
          this._cuposMetaServicios[cupoId].forEach(s => (s.numCupos -= decremento));
        });
      }
    },
  },
});
